import { Close } from "@/components/Icons/utility/Close.tsx";
import { RangePicker } from "@/components/Inputs/RangePicker/RangePicker.tsx";
import { Loader } from "@/components/Loader/Loader.tsx";
import { AvailabilityPopoverContent } from "@/components/Popover/AvailabilityPopoverContent.tsx";
import { PopoverContentProps } from "@/components/Popover/PopoverContentProps.ts";
import { CartAccommodationItems } from "@/components/ReservationSidebar/components/CartAccommodationItems.tsx";
import { ReservationSidebarProps } from "@/components/ReservationSidebar/ReservationSidebarProps.ts";
import { useReservationSidebarData } from "@/components/ReservationSidebar/useReservationSidebarData.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useIsMobile } from "@/helpers/useIsMobile.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useValidFormStore } from "@/stores/ValidFormStore/ValidFormStore.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "react-tiny-popover";

export const ReservationSidebar = (props: ReservationSidebarProps) => {
  const isMobile = useIsMobile();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const {
    page,
    defaultDateRange,
    setDefaultDateRange,
    cartAccommodationItems,
    calculateCartLoading,
    calculatedCartData,
    isCartItemInDisabledRateGroupFromRootAttributes,
  } = useReservationSidebarData(isMobile);
  const { t } = useTranslation();
  const { handleCloseCartDrawer } = useCartDrawerState();
  const isValid = useValidFormStore((state) => state.isValid);

  const isContinueDisabled = useMemo(() => {
    return (
      !cartAccommodationItems?.length ||
      props?.continueDisabled ||
      !isValid ||
      isCartItemInDisabledRateGroupFromRootAttributes ||
      !!calculatedCartData?.error
    );
  }, [
    cartAccommodationItems?.length,
    isValid,
    props?.continueDisabled,
    isCartItemInDisabledRateGroupFromRootAttributes,
    calculatedCartData?.error,
  ]);

  const renderTotalPrice = () => {
    let totalPrice = formatCurrency(0);
    if (calculatedCartData?.priceBreakdown?.withDiscount)
      totalPrice = formatCurrency(calculatedCartData.priceBreakdown?.withDiscount);
    if (calculatedCartData?.error) totalPrice = calculatedCartData?.error || t("error");
    if (cartAccommodationItems.length === 0) totalPrice = formatCurrency(0);

    return (
      <div className="c-reservation-sidebar__total">
        <p className="u-h5 u-fw-600 c-reservation-sidebar__total-label">{t("total")}</p>
        <div
          className={`u-h5 u-fw-600 c-reservation-sidebar__total-price ${
            calculatedCartData?.error ? "c-reservation-sidebar__total-error-price" : undefined
          }`}
        >
          {calculateCartLoading ? <Loader small loading /> : totalPrice}
        </div>
      </div>
    );
  };

  return (
    <div className="c-reservation-sidebar-wrapper">
      <div className="c-reservation-sidebar-wrapper__close">
        <button className="c-close-button" onClick={handleCloseCartDrawer}>
          <Close />
        </button>
      </div>
      <div className="c-reservation-sidebar">
        <header className="c-reservation-sidebar__header">
          <h5 className="u-h5 u-fw-600 c-reservation-sidebar__title">{t("yourRes")}</h5>
          {!props.hideDatePicker && (
            <RangePicker
              onChange={(dates) => setDefaultDateRange(dates)}
              startDate={defaultDateRange[0]}
              endDate={defaultDateRange[1]}
              disabled={page !== AppPage.AccommodationPicking || props.isReserved}
            />
          )}
        </header>
        <div className="c-reservation-sidebar__content">
          <CartAccommodationItems hideRemoveRate={props.hideRemoveRate} isReserved={props.isReserved} />
        </div>
        <footer className="c-reservation-sidebar__footer">
          {renderTotalPrice()}
          {!props.isReserved ? (
            <Popover
              isOpen={popoverOpen}
              content={({ position, childRect, popoverRect }: PopoverContentProps) => (
                <AvailabilityPopoverContent
                  popoverRect={popoverRect}
                  childRect={childRect}
                  position={position}
                  text={props.continueBtnPopoverText}
                />
              )}
            >
              <button
                className="c-be-button"
                disabled={isContinueDisabled}
                onClick={props.onContinue}
                onMouseOver={() => props.continueBtnPopoverText && setPopoverOpen(true)}
                onMouseLeave={() => props.continueBtnPopoverText && setPopoverOpen(false)}
              >
                <span className="c-be-button__label">
                  {props.continueButtonText
                    ? props.continueButtonText
                    : // custom text for simuni
                    isCartItemInDisabledRateGroupFromRootAttributes
                    ? t("inquiryOnly")
                    : t("continueWithRes")}
                </span>
              </button>
            </Popover>
          ) : null}
        </footer>
      </div>
    </div>
  );
};
